import React from 'react';
import Link from 'next/link';
import AssessmentFooter from '../AssessmentFooter';
import { CheckIcon } from '../icons';
import { NewLogo } from '@/components/ui/icons/Logo';
import { useAssessmentContent } from '../content/context';

interface ConfirmScreenProps {
  score: number;
  tier: string;
  firstName: string;
  email: string;
}

const ConfirmScreen: React.FC<ConfirmScreenProps> = ({ score, tier, firstName, email }) => {
  const { navPrimary, confirm } = useAssessmentContent();
  const tierLabel = tier.replace(' ▲', '').replace(' ★', '');
  // The email replaces the "your email" placeholder in the body copy.
  const body = email ? confirm.body.replace('{{email}}', email) : confirm.body;

  return (
    <div className="animate-fade-in">
      <nav
        className="bg-white border-b border-a-border px-10 flex items-center justify-between sticky top-0 z-50"
        style={{ height: '58px' }}
      >
        <Link href="/">
          <NewLogo height="60" title="Foxbox Digital" />
        </Link>
        <div className="flex items-baseline" style={{ gap: '5px' }}>
          <span
            className="font-extrabold text-a-navy font-sans uppercase"
            style={{ fontSize: '13px', letterSpacing: '0.06em' }}
          >
            {navPrimary}
          </span>
          <span
            className="font-medium text-a-navy font-sans uppercase"
            style={{ fontSize: '13px', letterSpacing: '0.06em' }}
          >
            {confirm.navSecondary}
          </span>
        </div>
      </nav>

      <div
        className="bg-a-off-white flex flex-col items-center justify-start p-8 sm:p-12"
        style={{ minHeight: 'calc(100vh - 58px - 42px)' }}
      >
        {/* Top card */}
        <div
          className="bg-white border border-a-border w-full text-center mb-6"
          style={{
            borderRadius: '10px',
            padding: '36px 40px',
            maxWidth: '600px',
          }}
        >
          {/* Check circle */}
          <div
            className="rounded-full mx-auto mb-5 flex items-center justify-center"
            style={{
              width: '56px',
              height: '56px',
              background: 'rgba(248,129,67,0.1)',
            }}
          >
            <CheckIcon />
          </div>

          <h2 className="font-extrabold text-a-navy font-sans mt-0 mb-2.5" style={{ fontSize: '22px' }}>
            {confirm.title}
            {firstName ? `, ${firstName}` : ''}.
          </h2>
          <p className="text-a-text-secondary mb-6" style={{ fontSize: '13px', lineHeight: '1.65' }}>
            {body}
          </p>

          {/* Score recap */}
          <div
            className="flex items-center justify-center gap-3"
            style={{
              background: '#f5f6f8',
              borderRadius: '8px',
              padding: '12px 16px',
            }}
          >
            <div className="font-extrabold text-a-orange font-sans leading-none" style={{ fontSize: '28px' }}>
              {score}
            </div>
            <div className="flex-shrink-0 bg-a-border" style={{ width: '1px', height: '36px' }} />
            <div className="text-left">
              <strong className="block font-bold text-a-navy" style={{ fontSize: '13px' }}>
                {tierLabel}
              </strong>
              <span className="text-a-text-muted" style={{ fontSize: '11px' }}>
                {confirm.tierCaption}
              </span>
            </div>
          </div>
        </div>

        {/* HubSpot Meetings embed */}
        <div
          className="bg-white border border-a-border w-full overflow-hidden"
          style={{ borderRadius: '10px', maxWidth: '600px' }}
        >
          <div className="px-5 pt-5 pb-2 border-b border-a-border">
            <div
              className="font-bold text-a-text-muted uppercase mb-1"
              style={{ fontSize: '9px', letterSpacing: '0.08em' }}
            >
              {confirm.meetingEyebrow}
            </div>
            <div className="font-bold text-a-navy" style={{ fontSize: '14px' }}>
              {confirm.meetingTitle}
            </div>
            <div className="text-a-text-muted mt-0.5" style={{ fontSize: '11px' }}>
              {confirm.meetingMeta}
            </div>
          </div>

          <iframe
            src={`${process.env.MEETINGS_URL}?embed=true`}
            style={{
              width: '100%',
              height: '700px',
              border: 'none',
              display: 'block',
            }}
            title={confirm.meetingEyebrow}
          />
        </div>

        <span
          className="text-a-text-muted cursor-pointer inline-block mt-4 underline"
          style={{ fontSize: '11px', textUnderlineOffset: '2px' }}
        >
          {confirm.skipLabel}
        </span>
      </div>

      <AssessmentFooter />
    </div>
  );
};

export default ConfirmScreen;
